import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { PageHeader, StatCard, Section } from "../../components/Common";
import { useAuth } from "../../lib/auth";
import { Link } from "react-router-dom";
import { ClipboardList, BookOpen, Trophy, Sparkles, Wallet, Users as UsersIcon } from "lucide-react";
import { PerformanceChart } from "../../components/Charts";
import { GamificationCard } from "../../components/GamificationCard";
import { NoticeBoard } from "../../components/NoticeBoard";
import { formatCurrency } from "../../lib/currency";
import { useDataSync } from "../../lib/realtime";

export default function StudentOverview() {
  const { user } = useAuth();
  const [tests, setTests] = useState([]);
  const [hw, setHw] = useState([]);
  const [attempts, setAttempts] = useState([]);
  const [att, setAtt] = useState([]);
  const [fees, setFees] = useState([]);
  const [batches, setBatches] = useState([]);

  const load = async () => {
    try {
      const [t, h, a, ah, f, b] = await Promise.all([
        api.get("/tests"),
        api.get("/homework"),
        api.get("/student/attempts"),
        api.get("/student/attendance"),
        api.get("/student/fees"),
        api.get("/student/batches"),
      ]);
      setTests(t.data); setHw(h.data); setAttempts(a.data); setAtt(ah.data); setFees(f.data); setBatches(b.data);
    } catch (e) { /* ignore */ }
  };
  useEffect(() => { load(); }, []);
  useDataSync(["batches", "homework", "tests", "fees", "attendance", "attempts"], () => load());

  const avgPercent = attempts.length ? (attempts.reduce((s, a) => s + (a.percent || 0), 0) / attempts.length).toFixed(1) : "—";
  const present = att.filter((a) => a.status === "present").length;
  const total = att.length;
  const presentPct = total ? Math.round((present / total) * 100) : 0;
  const pendingFees = fees.filter((f) => f.status !== "paid").reduce((s, f) => s + f.amount, 0);

  return (
    <div>
      <PageHeader title={`Hi, ${user.name.split(" ")[0]} 👋`} subtitle={`Subscription: ${user.subscription_plan || "Free"}`} testId="student-overview" />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label="Avg score" value={`${avgPercent}${avgPercent !== "—" ? "%" : ""}`} icon={Trophy} accent="yellow" />
        <StatCard label="Attendance" value={`${presentPct}%`} sub={`${present}/${total} days`} icon={ClipboardList} accent="mint" />
        <StatCard label="Pending tasks" value={hw.length} sub="homework items" icon={BookOpen} />
        <StatCard label="Pending fees" value={formatCurrency(pendingFees)} icon={Wallet} accent="coral" />
      </div>

      {/* Notice board */}
      <div className="mt-6">
        <NoticeBoard />
      </div>

      {/* Assigned batches */}
      <div className="mt-6">
        <Section title="My batches" actions={<span className="text-xs text-slate-500">{batches.length} assigned</span>}>
          {batches.length === 0 ? (
            <p className="text-sm text-slate-500" data-testid="student-no-batches">Ask admin to enroll you in a batch to see tests and homework.</p>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {batches.map((b) => (
                <div key={b.id} data-testid={`student-batch-${b.id}`} className="p-4 rounded-xl border border-slate-200 bg-white">
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-lg bg-indigo-50 text-indigo-600 grid place-items-center shrink-0">
                      <UsersIcon className="h-4 w-4" />
                    </div>
                    <div className="min-w-0">
                      <div className="font-heading font-bold text-slate-900 truncate">{b.name}</div>
                      <div className="text-xs text-slate-500 truncate">{b.subject}</div>
                      {b.teacher_name && <div className="text-xs text-slate-500 mt-1">Teacher: <span className="font-semibold text-slate-700">{b.teacher_name}</span></div>}
                      {(b.schedule_days || b.schedule_time) && (
                        <div className="text-xs text-slate-500 mt-1">{[b.schedule_days, b.schedule_time].filter(Boolean).join(" · ")}</div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Section>
      </div>

      <div className="mt-8 grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <Section title="Performance trend">
            <PerformanceChart attempts={attempts} />
          </Section>
        </div>
        <GamificationCard />
      </div>

      <div className="mt-6 grid lg:grid-cols-2 gap-6">
        <Section title="Upcoming tests">
          <div className="space-y-2">
            {tests.slice(0, 5).map((t) => (
              <Link key={t.id} to={`/dashboard/student/tests/${t.id}`} className="flex items-center justify-between p-3 rounded-lg hover:bg-slate-50">
                <div>
                  <div className="font-semibold text-slate-900">{t.title}</div>
                  <div className="text-xs text-slate-500">{t.questions.length} questions · {t.duration_minutes} min</div>
                </div>
                <span className="text-sm font-semibold text-blue-600">Start →</span>
              </Link>
            ))}
            {tests.length === 0 && <p className="text-sm text-slate-500">No tests yet.</p>}
          </div>
        </Section>

        <Section title="Recent results">
          <div className="space-y-2">
            {attempts.slice(0, 5).map((a) => (
              <div key={a.id} className="flex items-center justify-between p-3 rounded-lg hover:bg-slate-50">
                <div>
                  <div className="font-semibold text-slate-900">{a.test_title}</div>
                  <div className="text-xs text-slate-500">{a.score}/{a.total}{a.published === false ? " · pending publish" : ""}</div>
                </div>
                <span className={`text-sm font-bold ${a.percent >= 70 ? "text-emerald-600" : a.percent >= 40 ? "text-amber-600" : "text-rose-600"}`}>
                  {a.percent}%
                </span>
              </div>
            ))}
            {attempts.length === 0 && <p className="text-sm text-slate-500">Attempt your first test!</p>}
          </div>
        </Section>
      </div>

      {/* My fees */}
      <div className="mt-6">
        <Section title="My fees" actions={<span className="text-xs text-slate-500">{fees.filter((f) => f.status !== "paid").length} pending</span>}>
          <div className="divide-y divide-slate-100" data-testid="student-fees-list">
            {fees.map((f) => (
              <div key={f.id} data-testid={`student-fee-${f.id}`} className="flex items-center justify-between py-3">
                <div>
                  <div className="font-semibold text-slate-900">{formatCurrency(f.amount, f.currency)}</div>
                  <div className="text-xs text-slate-500">Due {f.due_date} · {f.description || "Tuition"}</div>
                </div>
                <span className={`text-xs font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${
                  f.status === "paid" ? "bg-emerald-50 text-emerald-700" :
                  f.status === "overdue" ? "bg-rose-50 text-rose-700" : "bg-amber-50 text-amber-700"
                }`}>{f.status}</span>
              </div>
            ))}
            {fees.length === 0 && <p className="text-sm text-slate-500 py-4">No fees on record.</p>}
          </div>
        </Section>
      </div>

      <div className="mt-6">
        <Link to="/dashboard/student/insights" className="block bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl p-6 ym-card-hover">
          <Sparkles className="h-6 w-6" />
          <h3 className="mt-3 font-heading font-bold text-xl">Get AI-powered performance insights</h3>
          <p className="mt-1 text-blue-100 text-sm">Personalised tips and a detailed report card from Claude.</p>
        </Link>
      </div>
    </div>
  );
}
